import {FC, useCallback} from 'react';
import {styled} from 'styled-components';

import {MEDIA_QUERY_MOBILE_ONLY} from '@shared_web/breakpoints';
import {Button} from '@shared_web/components/core/button';
import {Flex} from '@shared_web/components/core2/flex';

import {Translate, translate} from '@mavismart_frontend/components/translate';
import {
  BUTTON_THEME,
  BUTTON_THEME2,
  LIGHT_GRAY_COLOR,
  MAIN_COLOR,
  TEXT_MAIN_COLOR,
} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';

interface ExplodeControlsProps {
  /** 0 = assembled, 100 = fully exploded. */
  percent: number;
  onPercentChange: (percent: number) => void;
  onRecenter: () => void;
  disabled: boolean;
}

export const ExplodeControls: FC<ExplodeControlsProps> = props => {
  const {percent, onPercentChange, onRecenter, disabled} = props;
  const account = useAccount();
  const language = getUserLanguage(account);

  const handleAssemble = useCallback(() => onPercentChange(0), [onPercentChange]);
  const handleExplode = useCallback(() => onPercentChange(100), [onPercentChange]);
  const handleSlider = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onPercentChange(Number(event.target.value)),
    [onPercentChange]
  );

  return (
    <Wrapper $column $gap="s">
      <ButtonRow $gap="s" $wrap>
        <Button
          themeOverrides={percent === 0 ? BUTTON_THEME : BUTTON_THEME2}
          disabled={disabled}
          onClick={handleAssemble}
        >
          <Translate value="door_3d__assemble" />
        </Button>
        <Button
          themeOverrides={percent === 100 ? BUTTON_THEME : BUTTON_THEME2}
          disabled={disabled}
          onClick={handleExplode}
        >
          <Translate value="door_3d__explode" />
        </Button>
        <Button themeOverrides={BUTTON_THEME2} disabled={disabled} onClick={onRecenter}>
          <Translate value="door_3d__recenter" />
        </Button>
      </ButtonRow>

      <SliderRow>
        <Slider
          type="range"
          min={0}
          max={100}
          step={1}
          value={percent}
          disabled={disabled}
          onChange={handleSlider}
          aria-label={translate('door_3d__explode_level', language)}
          aria-valuetext={`${percent}%`}
        />
        <SliderValue>{percent}%</SliderValue>
      </SliderRow>
    </Wrapper>
  );
};

ExplodeControls.displayName = 'ExplodeControls';

const Wrapper = styled(Flex)`
  min-width: 0;
`;

const ButtonRow = styled(Flex)`
  ${MEDIA_QUERY_MOBILE_ONLY} {
    gap: 4px;

    button {
      font-size: 12px;
      padding: 4px 8px;
      min-height: unset;
    }
  }
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${MEDIA_QUERY_MOBILE_ONLY} {
    gap: 6px;
  }
`;

const Slider = styled.input`
  flex-grow: 1;
  min-width: 0;
  accent-color: ${MAIN_COLOR};
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: solid 2px ${LIGHT_GRAY_COLOR};
    outline-offset: 3px;
  }
`;

const SliderValue = styled.span`
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${TEXT_MAIN_COLOR}aa;
  min-width: 38px;
  text-align: right;
`;
